import { Icon24ErrorCircle } from '@vkontakte/icons';
import { differenceInCalendarDays, fromUnixTime } from 'date-fns';
import { plural } from 'lib/plural';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    B2B_PUBLIC_FOLDER_END_TIME,
    B2B_USER_BLOCK_TIME,
    IS_B2B_BIZ_USER,
    IS_BLOCK_ADD_USER_B2B_TARIFF,
    IS_BLOCKED_B2B_PUBLIC_FOLDER,
} from 'reactApp/appHelpers/configHelpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import {
    clearOverquotaMessage,
    getOverquotaMessage,
    getOverquotaMessageInfo,
    setOverquotaMessage,
} from 'reactApp/ui/HomePageInformer/HomePageInformer.helpers';
import { type IProps as InformerProps, EInformerColor } from 'reactApp/ui/Informer/Informer.types';

const ICON_SIZE = 32;

type HomePageInformerData = Omit<InformerProps, 'children'> & { message: string };

export function useGetHomePageInformerData() {
    const cloudSpace = useSelector(UserSelectors.getCloudSpace);
    const storedOverquotaMessage = getOverquotaMessage();
    const overquotaMessage = getOverquotaMessageInfo(cloudSpace.usedPercent);
    const [isShowOverquotaInformer, setIsShowOverquotaInformer] = useState(
        !!overquotaMessage && IS_B2B_BIZ_USER && overquotaMessage !== storedOverquotaMessage
    );

    const daysBeforeUserBlock = useMemo(
        () => (B2B_USER_BLOCK_TIME ? differenceInCalendarDays(fromUnixTime(B2B_USER_BLOCK_TIME), new Date()) : -1),
        []
    );

    const daysBeforeBlockPublicFolders = useMemo(
        () => (B2B_PUBLIC_FOLDER_END_TIME ? differenceInCalendarDays(fromUnixTime(B2B_PUBLIC_FOLDER_END_TIME), new Date()) : -1),
        []
    );

    useEffect(() => {
        if ((overquotaMessage && storedOverquotaMessage && overquotaMessage !== storedOverquotaMessage) || !overquotaMessage) {
            clearOverquotaMessage();
        }

        setIsShowOverquotaInformer(!!overquotaMessage && IS_B2B_BIZ_USER && overquotaMessage !== storedOverquotaMessage);
    }, [overquotaMessage, storedOverquotaMessage]);

    return useMemo<HomePageInformerData | null>(() => {
        if (IS_BLOCK_ADD_USER_B2B_TARIFF && daysBeforeUserBlock >= 0) {
            return {
                color: EInformerColor.red,
                icon: <Icon24ErrorCircle width={ICON_SIZE} height={ICON_SIZE} fill="var(--vkui--color_text_negative--hover)" />,
                title: `Диск может быть заблокирован через ${daysBeforeUserBlock} ${['день', 'дня', 'дней'][plural(daysBeforeUserBlock)]}`,
                message: 'Обратитесь к администратору домена, чтобы сохранить доступ',
            };
        }

        if (isShowOverquotaInformer) {
            return {
                color: EInformerColor.red,
                closable: true,
                icon: <Icon24ErrorCircle width={ICON_SIZE} height={ICON_SIZE} fill="var(--vkui--color_text_negative--hover)" />,
                message: overquotaMessage as string,
                onClose: () => {
                    setOverquotaMessage(overquotaMessage);
                    setIsShowOverquotaInformer(false);
                },
            };
        }

        if (IS_BLOCKED_B2B_PUBLIC_FOLDER && !!B2B_PUBLIC_FOLDER_END_TIME && daysBeforeBlockPublicFolders >= 0) {
            const blockTime =
                daysBeforeBlockPublicFolders === 0
                    ? 'в течение суток'
                    : `через ${daysBeforeBlockPublicFolders} ${['день', 'дня', 'дней'][plural(daysBeforeBlockPublicFolders)]}`;
            return {
                color: EInformerColor.yellow,
                icon: <Icon24ErrorCircle width={ICON_SIZE} height={ICON_SIZE} fill="var(--vkui--color_accent_orange)" />,
                title: `Общие папки могут быть заблокированы ${blockTime}`,
                message: 'Обратитесь к администратору домена, чтобы сохранить доступ',
            };
        }

        return null;
    }, [daysBeforeBlockPublicFolders, daysBeforeUserBlock, isShowOverquotaInformer, overquotaMessage]);
}
